/* =============================================================================
   CORE / OPERATOR-ROLES — Single-Source der Rechte-Matrix (S131, Phase D)
   =============================================================================
   EINE Wahrheit für „wer darf welche View sehen + welche Aktion auslösen".
   Spiegelt 1:1 die server-seitige Durchsetzung (phase_97: assert_operator_role)
   — der Server ist autoritativ, dieses Modul ist die UI-Schicht davor
   (View-Gating + freundliche Hinweise). Keine verstreuten Rollen-Strings mehr.

   ROLLEN-CLEAN-CUT (Jonas-Entscheid 2026-06-11):
     manager  — „Manager"
     service  — „Service" (bedient die Gäste)            [Token vorher = staff]
     staff    — „Staff"   (Küche/Theke/Lieferung, Display) [Token umgewidmet]
     owner/admin — Firmen-Ebene, überlagern ALLES.
   `host` ist abgeschafft (war ungenutzt).

   MATRIX (Quelle: NEXT_SESSION.md / docs Rechte-Matrix):
     | Rolle   | Views                                    | Aktionen                         |
     | manager | Heute·Operativ·Plan&Karte·Auswertung +   | alles operativ inkl. Storno,     |
     |         | System (READ-ONLY)                       | Z-Bon (UI), Fahrer zuweisen      |
     | service | Heute·Operativ                           | aufnehmen·Status·kassieren·      |
     |         |                                          | Reservierung·Walk-In             |
     | staff   | NUR Heute (Dashboard reduziert: 3        | Display: channel-status·delivery·|
     |         | Displays + Operator-Kachel)              | Fahrer zuweisen                  |
   Z-Bon/Bons/Mails/TSE laufen System-unabhängig (NICHT operator-gegated).
============================================================================ */

/** Sichtbare Operator-Rollen-Labels (Token -> Anzeige). Single-Source. */
export const ROLE_LABELS = Object.freeze({
  owner:   'Owner',
  admin:   'Admin',
  manager: 'Manager',
  service: 'Service',
  staff:   'Staff',
  // S167 (Jonas) — reine Warenwirtschafts-Rolle: sieht NUR den Ware-Tab und dort
  // nur Lager/Bestellungen/Schwund/Chargen (siehe GROUP_TAB_RESTRICTIONS).
  lager:   'Lager',
  // S209 (Jonas) — Fahrer-Rolle: KEINE Admin-Gruppe (VIEW_MATRIX driver:[]),
  // Einstieg ist die mobile Terminal-View. Löst den alten drivers-Stammdaten-
  // Pfad ab — Fahrer sind ab hier User mit Rolle driver (PIN-Login).
  driver:  'Fahrer'
});

/** Über die Team-UI vergebbare Rollen (owner gibt es nur per Bootstrap). */
export const ASSIGNABLE_ROLES = Object.freeze(['admin', 'manager', 'service', 'staff', 'lager', 'driver']);

/** Owner/Admin überlagern jede Matrix (Firmen-Ebene). */
const OVERLAY = new Set(['owner', 'admin']);

/** Sichtbare Admin-GRUPPEN (shell.js ADMIN_GROUPS ids) je Operator-Rolle. */
export const VIEW_MATRIX = Object.freeze({
  // S157 — „ware" (ERP/Warenwirtschaft) = Management-Sicht, gleiche Sichtbarkeit
  // wie „planning": owner/admin/manager, NICHT service/staff.
  // S167 — „planning" (Tischplan/Speisekarte) ist kein eigener Tab mehr, sondern
  // Untergruppe von „system"; daher hier entfallen (layout/menu erben system-Sicht).
  // S189 — Reihenfolge „Auswertung"(reports) VOR „Ware" (Jonas 2026-07-01).
  // S263 — „schedule" (Dienstplan-Maske) zwischen Operativ und Auswertung:
  // manager+ (Spec § 6.2). Service/Staff/Lager/Fahrer sehen den Reiter nicht.
  owner:   ['today', 'operations', 'schedule', 'reports', 'ware', 'system'],
  admin:   ['today', 'operations', 'schedule', 'reports', 'ware', 'system'],
  manager: ['today', 'operations', 'schedule', 'reports', 'ware', 'system'],
  service: ['today', 'operations'],
  staff:   ['today'],
  // S167/S168 — „Lager" sieht NUR die Ware-Gruppe (innerhalb gegated, s.u.).
  // KEIN „today": der Lager-Operator soll bewusst auch die Küchen-/Theken-/
  // Liefer-Cockpits (Dashboard-Kachel-Triade) NICHT öffnen können (Jonas S168).
  // Der frühere „Trap" (kein Weg aus der Operator-Session) ist stattdessen über
  // den IMMER sichtbaren Operator-Chip in der Topbar gelöst (shell.js) — der
  // Wechsel/Abmelden ist tab-rechte-unabhängig erreichbar.
  lager:   ['ware'],
  // S209 — „Fahrer" sieht KEINE Admin-Gruppe. Einstieg ist die mobile
  // driver-terminal-View (Auto-Einstieg nach PIN-Login). Der Operator-Chip in
  // der Terminal-Topbar bleibt IMMER erreichbar → kein Trap (S168-Muster).
  driver:  []
});

/**
 * S167 — Sub-Tab-Einschränkung INNERHALB einer Gruppe je Rolle. Ohne Eintrag
 * sieht die Rolle alle Tabs der (sichtbaren) Gruppe. „Lager" sieht im Ware-Tab
 * nur Lager/Bestellungen/Schwund/Chargen (NICHT Rubriken/Zutaten/Food-Cost/
 * Lieferanten). Reihenfolge folgt der ADMIN_GROUPS-Definition, nicht dieser Liste.
 */
export const GROUP_TAB_RESTRICTIONS = Object.freeze({
  lager: { ware: ['lager', 'purchase-orders', 'shrinkage', 'lots'] }
});

/** Erlaubte Tab-Ids einer Rolle in einer Gruppe, oder null = keine Einschränkung. */
export function restrictedTabIds(role, groupId) {
  const r = GROUP_TAB_RESTRICTIONS[role];
  return (r && r[groupId]) ? r[groupId] : null;
}

/** Rollen, die die System-Gruppe NUR LESEN dürfen (Felder/Buttons disabled). */
const SYSTEM_READONLY_ROLES = new Set(['manager']);

/**
 * Rollen, für die das Dashboard im „Nur-Ansicht"-Modus rendert (S131b):
 * das VOLLE Dashboard 1:1 wie alle Rollen, aber bedienbar sind nur die
 * Operator-Kachel (+ die Display-Pills in der Topbar) — alles andere
 * (Quick-Actions, Anfragen, Kapazitäts-Drilldown, Heatmap, „Als Nächstes")
 * wird gedimmt + gesperrt. Staff bedient die Küche/Theke/Lieferung über die
 * Displays, nicht über die Kanban-/Kassen-Interaktionen des Dashboards.
 */
// S168 — „lager" sieht das Dashboard gar nicht (kein „today", s. VIEW_MATRIX) →
// kein Locked-Sonderfall nötig. Nur „staff" rendert das Dashboard read-only.
const DASHBOARD_LOCKED_ROLES = new Set(['staff']);

/**
 * Aktions-Matrix (Action-Key -> erlaubte Rollen). Spiegelt phase_97 exakt:
 *   operativ  -> assert_operator_role(..., ['manager','service'])
 *   channel   -> ['manager','service','staff']
 *   delivery  -> ['manager','staff']
 *   storno    -> ['manager']
 * owner/admin sind via OVERLAY immer erlaubt (nicht hier gelistet).
 */
export const ACTION_MATRIX = Object.freeze({
  'order.create':       ['manager', 'service'],
  'order.status':       ['manager', 'service'],
  'order.pay':          ['manager', 'service'],
  'reservation.create': ['manager', 'service'],
  // S239 Cut A0 — „✅ Gäste sind da": Check-in erzeugt die Belegung an der
  // Reservierung. Server-fest in `checkin_reservation`
  // (assert_operator_role(['manager','service'])); owner/admin via Overlay.
  'reservation.checkin': ['manager', 'service'],
  'walkin.create':      ['manager', 'service'],
  'occupancy.end':      ['manager', 'service'],
  // S243 Cut A — Schloss „Handy-Bestellung am Tisch" 🔓/🔒 an einer Belegung.
  // Server-fest in `set_occupancy_self_order`
  // (assert_operator_role(['manager','service'])); owner/admin via Overlay.
  // Das Rotieren des QR-Tokens ist bewusst NICHT hier: es macht gedruckte
  // Aufsteller ungültig und ist damit Stammdaten-Pflege (manager-only, gegated
  // über requireEditPermission('tables') + assert_operator_role(['manager'])).
  'occupancy.selforder': ['manager', 'service'],
  'channel.status':     ['manager', 'service', 'staff'],
  // S209 — Fahrer schalten den Liefer-Status ihrer eigenen Orders fort
  // (Unterwegs → Zugestellt). Server-fest: update_delivery_status gate't
  // ['manager','staff','driver'] + Driver-Scope (nur eigene Orders, nur die
  // Übergänge assigned→out_for_delivery→delivered).
  'delivery.status':    ['manager', 'staff', 'driver'],
  // Fahrer weisen sich NICHT selbst zu → driver.assign bleibt manager/staff.
  'driver.assign':      ['manager', 'staff'],
  'order.storno':       ['manager'],
  'zbon':               ['manager'],
  // E1 — Lager-Buchungen (Wareneingang/Korrektur/Schwund/Inventur/Mindestbestand):
  // nur Manager (owner/admin via Overlay). Server-fest in den phase_118-RPCs
  // (assert_operator_role(..., ['manager'])).
  // S167 — „Lager"-Rolle darf Warenwirtschafts-Buchungen (Wareneingang/
  // Korrektur/Schwund/Inventur/Mindestbestand/Chargen-Ausbuchen) auslösen.
  // Server-fest: die ware-RPCs akzeptieren ['manager','lager'] (phase_127).
  'stock.movement':     ['manager', 'lager'],
  // S185 Cut 5 — Fixkosten-Stammdaten (operating_costs) editieren: nur Manager
  // (owner/admin via Overlay). Spiegelt die phase_142-RLS (owner/admin/manager).
  // NICHT „lager" (Fixkosten sind Management-/Kostensicht, kein Lagerthema).
  'operatingcosts.edit': ['manager']
});

/* -------------------------------------------------------------------------- */
/* S222 — Operator-Idle-Timeouts pro Rolle (Team-Tab)                          */
/* -------------------------------------------------------------------------- */

/** Default-Idle-Timeout je Rolle in Minuten (0 = aus). Ein Fahrer ist unterwegs
 *  — Handy-Standby zwischen Stopps ist NORMAL-Betrieb, kein Idle → driver 0;
 *  sein Session-Ende ist Schichtende/Operator-Wechsel. Alle anderen Rollen 15.
 *  Fehlende Keys im Settings-Slot fallen hierauf zurück (kein Persistenz-
 *  Migrations-Zwang, rein über den pure Helper). */
export const DEFAULT_ROLE_TIMEOUTS = Object.freeze({
  owner: 15, admin: 15, manager: 15, service: 15, staff: 15, lager: 15, driver: 0
});

const ROLE_TIMEOUT_MIN = 0;
const ROLE_TIMEOUT_MAX = 240;

function _clampRoleTimeout(n) {
  const v = Math.round(Number(n) || 0);
  if (v < ROLE_TIMEOUT_MIN) return ROLE_TIMEOUT_MIN;
  if (v > ROLE_TIMEOUT_MAX) return ROLE_TIMEOUT_MAX;
  return v;
}

/**
 * Pure: geclampte Idle-Timeout-Minuten für eine Rolle (Vitest-getestet).
 *   - gültiger Settings-Wert (≥0, finite) → clamp 0–240
 *   - fehlend / NaN / negativ → Rollen-Default (unbekannte Rolle → 15,
 *     driver → 0)
 *   - 0 bleibt 0 (= automatische Sperre aus)
 * `settingsSlot` = settings.operatorRoleTimeouts ({} wenn nie gesetzt).
 */
export function roleIdleTimeoutMinutes(role, settingsSlot) {
  const fallback = Object.prototype.hasOwnProperty.call(DEFAULT_ROLE_TIMEOUTS, role)
    ? DEFAULT_ROLE_TIMEOUTS[role]
    : 15;
  const slot = (settingsSlot && typeof settingsSlot === 'object') ? settingsSlot : {};
  const raw = slot[role];
  const n = Number(raw);
  if (raw == null || raw === '' || !Number.isFinite(n) || n < 0) {
    return _clampRoleTimeout(fallback);
  }
  return _clampRoleTimeout(n);
}

/* -------------------------------------------------------------------------- */
/* Pure Helpers (Vitest)                                                       */
/* -------------------------------------------------------------------------- */

/** Token -> Label (Fallback: Token selbst). */
export function roleLabel(role) {
  return ROLE_LABELS[role] || role || '';
}

/**
 * Effektive Operator-Rolle fürs Gating. OHNE angemeldeten Operator gilt die
 * restriktivste Sicht „staff" (Jonas/Cowork-Entscheid: Displays sichtbar,
 * jede Aktion → PIN-Overlay). `operator` = window.App.operator.getOperator().
 */
export function effectiveRole(operator) {
  const r = operator && operator.role;
  return (r && ROLE_LABELS[r]) ? r : 'staff';
}

/** Sichtbare Gruppen-Ids für eine Rolle (Fallback: staff). */
export function visibleGroupIds(role) {
  return VIEW_MATRIX[role] || VIEW_MATRIX.staff;
}

/** Darf die Rolle die Gruppe sehen? */
export function canSeeGroup(role, groupId) {
  return visibleGroupIds(role).includes(groupId);
}

/** Ist die System-Gruppe für die Rolle nur lesbar? (owner/admin: nein) */
export function isSystemReadOnly(role) {
  if (OVERLAY.has(role)) return false;
  return SYSTEM_READONLY_ROLES.has(role);
}

/** Rendert das Dashboard für die Rolle im „Nur-Ansicht"-Modus (nur Operator-
 *  Kachel bedienbar)? (owner/admin/manager/service: nein; staff: ja). */
export function isDashboardLocked(role) {
  if (OVERLAY.has(role)) return false;
  return DASHBOARD_LOCKED_ROLES.has(role);
}

/** Darf die Rolle das „Wer ist aktiv"-Presence-Widget sehen? (manager/owner/admin;
 *  Service/Staff: nein). Spiegelt den Server-Gate von list_active_operators. */
export function canSeeOperatorPresence(role) {
  if (OVERLAY.has(role)) return true;
  return role === 'manager';
}

/** Darf die Rolle die Aktion auslösen? owner/admin immer. */
export function can(role, action) {
  if (OVERLAY.has(role)) return true;
  const allowed = ACTION_MATRIX[action];
  return Array.isArray(allowed) && allowed.includes(role);
}

/* -------------------------------------------------------------------------- */
/* Multi-Role (S167): ein User kann MEHRERE Rollen tragen. `role` bleibt die    */
/* primäre Rolle (RLS/current_user_role-Backward-Compat), `roles[]` ist die volle */
/* Menge. Gating = UNION über alle Rollen.                                       */
/* -------------------------------------------------------------------------- */

const _GROUP_ORDER = ['today', 'operations', 'schedule', 'reports', 'ware', 'system'];  // S189 — reports vor ware · S263 — schedule dazwischen

/** Alle gültigen Rollen eines Operators. Fallback: [effectiveRole(operator)]. */
export function effectiveRoles(operator) {
  const list = (operator && Array.isArray(operator.roles)) ? operator.roles : null;
  const valid = (list || []).filter(r => ROLE_LABELS[r]);
  return valid.length ? valid : [effectiveRole(operator)];
}

/** Union der sichtbaren Gruppen über mehrere Rollen (stabile Reihenfolge). */
export function visibleGroupIdsForRoles(roles) {
  const set = new Set();
  (roles || []).forEach(r => visibleGroupIds(r).forEach(g => set.add(g)));
  return _GROUP_ORDER.filter(g => set.has(g));
}

/** Darf irgendeine der Rollen die Gruppe sehen? */
export function canSeeGroupAny(roles, groupId) {
  return (roles || []).some(r => canSeeGroup(r, groupId));
}

/** Erlaubte Tab-Ids einer Gruppe über mehrere Rollen (Union). null = keine
 *  Einschränkung (mind. eine die Gruppe sehende Rolle hat keine Restriktion). */
export function restrictedTabIdsForRoles(roles, groupId) {
  let anyUnrestricted = false;
  const set = new Set();
  (roles || []).forEach(r => {
    if (!canSeeGroup(r, groupId)) return;       // Rolle sieht die Gruppe nicht
    const only = restrictedTabIds(r, groupId);
    if (only === null) anyUnrestricted = true;  // diese Rolle sieht alle Tabs
    else only.forEach(t => set.add(t));
  });
  if (anyUnrestricted) return null;
  return set.size ? Array.from(set) : null;
}

/** Darf irgendeine der Rollen die Aktion auslösen? */
export function canRoles(roles, action) {
  return (roles || []).some(r => can(r, action));
}

/** System-Gruppe nur lesbar? Schreibbar, sobald eine Rolle owner/admin ist. */
export function isSystemReadOnlyForRoles(roles) {
  if ((roles || []).some(r => OVERLAY.has(r))) return false;
  return (roles || []).some(r => SYSTEM_READONLY_ROLES.has(r));
}

/** Dashboard „Nur-Ansicht"? Gesperrt nur, wenn ALLE today-fähigen Rollen
 *  gesperrt sind (reiner staff); eine Voll-Dashboard-Rolle macht es frei. */
export function isDashboardLockedForRoles(roles) {
  const todayRoles = (roles || []).filter(r => canSeeGroup(r, 'today'));
  if (!todayRoles.length) return false;
  return todayRoles.every(r => isDashboardLocked(r));
}

/** Bequemer Wrapper: Operator-Objekt -> alle Rollen -> can() (Union, Multi-Role). */
export function operatorCan(operator, action) {
  return canRoles(effectiveRoles(operator), action);
}

/* -------------------------------------------------------------------------- */
/* Global-Bridge für plain-script-Adapter / inline-Handler (kein ESM-Import)   */
/* -------------------------------------------------------------------------- */
if (typeof window !== 'undefined') {
  window.OperatorRoles = {
    ROLE_LABELS, ASSIGNABLE_ROLES, VIEW_MATRIX, ACTION_MATRIX,
    DEFAULT_ROLE_TIMEOUTS, roleIdleTimeoutMinutes,
    roleLabel, effectiveRole, visibleGroupIds, canSeeGroup, isSystemReadOnly, isDashboardLocked,
    canSeeOperatorPresence, can, operatorCan,
    GROUP_TAB_RESTRICTIONS, restrictedTabIds,
    // S167 Multi-Role
    effectiveRoles, visibleGroupIdsForRoles, canSeeGroupAny, restrictedTabIdsForRoles,
    canRoles, isSystemReadOnlyForRoles, isDashboardLockedForRoles
  };
}
